function formatLatNotam(input: number): string {
    const greaterThanZero = input > 0;
    input = Math.abs(input);
    const deg = Math.trunc(input);
    const minFrac = (input - deg) * 60;
    const minutes = Math.trunc(minFrac);
    const seconds = Math.trunc((minFrac - minutes) * 60);
    return `${deg.toFixed(0).padStart(2, '0')}${minutes.toFixed(0).padStart(2, '0')}${seconds.toFixed(0).padStart(2, '0')}${greaterThanZero ? 'N' : 'S'}`;
}

function formatLngNotam(input: number): string {
    const greaterThanZero = input > 0;
    input = Math.abs(input);
    const deg = Math.trunc(input);
    const minFrac = (input - deg) * 60;
    const minutes = Math.trunc(minFrac);
    const seconds = Math.trunc((minFrac - minutes) * 60);
    return `${deg.toString().padStart(3, '0')}${minutes.toString().padStart(2, '0')}${seconds.toString().padStart(2, '0')}${greaterThanZero ? 'E' : 'W'}`;
}

export function formatLatLngNotam(position: number[]): string {
    return `${formatLatNotam(position[1])} ${formatLngNotam(position[0])}`;
}

export function parsePositions(text: string): [number, number][] {
    if (text.includes(';')) {
        return parseFPM(text);
    } else if (text.includes(',')) {
        return parseAny(text);
    } else if (
        (text.includes('-') || text.includes('/') || text.includes(' ')) &&
        (text.includes('S') || text.includes('W') || text.includes('N') || text.includes('E'))
    ) {
        return parseNotam(text);
    }

    return [];
}
const notamLatitude = new RegExp(/^[0-9]{6}[N|S]$/);
const notamLongitude = new RegExp(/^[0-9]{7}[E|W]$/);
const decimalDegreesLatitude = new RegExp(/^[0-9]+[.]?[0-9]*[N|S]$/);
const decimalDegreesLongitude = new RegExp(/^[0-9]+[.]?[0-9]*[E|W]$/);
//"Degrees and decimal minutes (N23 27.402, W123 27.402)",
const decimalDegreesMinutesLatitude = new RegExp(/^[N|S][0-9]{0,3} [0-9]+[.]?[0-9]*$/);
const decimalDegreesMinutesLongitude = new RegExp(/^[E|W][0-9]{0,3} [0-9]+[.]?[0-9]*$/);
const decimalDegreesMinutesSecondsLatitude = new RegExp(/^[0-9]{0,3} [0-9]+ [0-9]+[.]?[0-9]*[N|S]$/);
const decimalDegreesMinutesSecondsLongitude = new RegExp(/^[0-9]{0,3} [0-9]+ [0-9]+[.]?[0-9]*[E|W]$/);

export function parseLat(text: string): number | null {
    text = text.trim().toUpperCase();

    if (notamLatitude.test(text)) {
        return parseNotamLatitude(text);
    }

    if (decimalDegreesLatitude.test(text)) {
        const num = Number.parseFloat(text.slice(0, text.length - 1));
        if (Number.isNaN(num)) {
            return null;
        }
        return num * (text[text.length - 1] === 'N' ? 1 : -1);
    }

    if (decimalDegreesMinutesLatitude.test(text)) {
        const indexOfSpace = text.indexOf(' ');
        if (indexOfSpace < 0) {
            return null;
        }

        const degrees = Number.parseInt(text.slice(1, indexOfSpace));
        if (Number.isNaN(degrees)) {
            return null;
        }

        const minutes = Number.parseFloat(text.slice(indexOfSpace + 1));
        if (Number.isNaN(minutes)) {
            return null;
        }

        return (text[0] === 'N' ? 1 : -1) * (degrees + minutes / 60);
    }

    if (decimalDegreesMinutesSecondsLatitude.test(text)) {
        const indexOfFirstSpace = text.indexOf(' ');
        const indexOfSecondSpace = text.indexOf(' ', indexOfFirstSpace + 1);
        if (indexOfFirstSpace < 0 || indexOfSecondSpace < 0) {
            return null;
        }

        const degrees = Number.parseInt(text.slice(0, indexOfFirstSpace));
        if (Number.isNaN(degrees)) {
            return null;
        }

        const minutes = Number.parseInt(text.slice(indexOfFirstSpace + 1, indexOfSecondSpace));
        if (Number.isNaN(minutes)) {
            return null;
        }

        const seconds = Number.parseFloat(text.slice(indexOfSecondSpace + 1, text.length - 1));
        if (Number.isNaN(seconds)) {
            return null;
        }

        return (text[text.length - 1] === 'N' ? 1 : -1) * (degrees + minutes / 60 + seconds / 3600);
    }

    const number = Number.parseFloat(text);
    if (!Number.isNaN(number)) {
        return number;
    }

    return null;
}

export function parseLng(text: string): number | null {
    text = text.trim().toUpperCase();

    if (notamLongitude.test(text)) {
        return parseNotamLongitude(text);
    }

    if (decimalDegreesLongitude.test(text)) {
        const num = Number.parseFloat(text.slice(0, text.length - 1));
        if (Number.isNaN(num)) {
            return null;
        }
        return num * (text[text.length - 1] === 'E' ? 1 : -1);
    }

    if (decimalDegreesMinutesLongitude.test(text)) {
        const indexOfSpace = text.indexOf(' ');
        if (indexOfSpace < 0) {
            return null;
        }

        const degrees = Number.parseInt(text.slice(1, indexOfSpace));
        if (Number.isNaN(degrees)) {
            return null;
        }

        const minutes = Number.parseFloat(text.slice(indexOfSpace + 1));
        if (Number.isNaN(minutes)) {
            return null;
        }

        return (text[0] === 'E' ? 1 : -1) * (degrees + minutes / 60);
    }

    if (decimalDegreesMinutesSecondsLongitude.test(text)) {
        const indexOfFirstSpace = text.indexOf(' ');
        const indexOfSecondSpace = text.indexOf(' ', indexOfFirstSpace + 1);
        if (indexOfFirstSpace < 0 || indexOfSecondSpace < 0) {
            return null;
        }

        const degrees = Number.parseInt(text.slice(0, indexOfFirstSpace));
        if (Number.isNaN(degrees)) {
            return null;
        }

        const minutes = Number.parseInt(text.slice(indexOfFirstSpace + 1, indexOfSecondSpace));
        if (Number.isNaN(minutes)) {
            return null;
        }

        const seconds = Number.parseFloat(text.slice(indexOfSecondSpace + 1, text.length - 1));
        if (Number.isNaN(seconds)) {
            return null;
        }
        return (text[text.length - 1] === 'E' ? 1 : -1) * (degrees + minutes / 60 + seconds / 3600);
    }

    const number = Number.parseFloat(text);
    if (!Number.isNaN(number)) {
        return number;
    }

    return null;
}

function parseAny(text: string): [number, number][] {
    const points: [number, number][] = [];
    let i = 0;
    let j = 0;
    for (const segment of text.split(/[;, \t\r\n]+/g)) {
        const trimmed = segment.trim();
        if (trimmed.length === 0) {
            continue;
        }

        const number = Number.parseFloat(trimmed);
        if (Number.isNaN(number)) {
            return [];
        }

        if (i % 2 === 0) {
            points[j] = [number, 0];
        } else {
            points[j][1] = number;
            j++;
        }
        i++;
    }

    return points;
}

function parseFPM(text: string): [number, number][] {
    const points: [number, number][] = [];
    let i = 0;
    let j = 0;
    for (const segment of text.split(/[;, \t\r\n]+/g)) {
        const trimmed = segment.trim();
        if (trimmed.length === 0) {
            continue;
        }

        const number = +segment / 600;
        if (i % 2 === 0) {
            points[j] = [number, 0];
        } else {
            points[j][1] = number;
            j++;
        }
        i++;
    }

    return points;
}

function parseNotam(text: string): [number, number][] {
    let i = 0;
    let j = 0;
    const points: [number, number][] = [];
    for (const segment of text.trim().split(/[;,\n \t\r]+/g)) {
        const trimmed = segment.trim();
        if (trimmed.length === 0) {
            continue;
        }

        if (i % 2 === 0) {
            points[j] = [parseNotamLatitude(trimmed), 0];
        } else {
            points[j][1] = parseNotamLongitude(trimmed);
            j++;
        }
        i++;
    }
    return points;
}

function parseNotamLatitude(text: string): number {
    let mult: number;
    let degMinSec = 0;
    if (text.match(/^[N|S]\d{3,7}$/g)) {
        degMinSec = parseDegMinSec(text.substring(1), 2);
        mult = text[0] === 'N' ? 1 : -1;
    } else if (text.match(/^\d{3,7}[N|S]$/g)) {
        degMinSec = parseDegMinSec(text.substring(0, text.length - 1), 2);
        mult = text[text.length - 1] === 'N' ? 1 : -1;
    } else {
        throw new Error('Unrecognized notam format');
    }
    return +(mult * degMinSec).toFixed(6);
}

function parseNotamLongitude(text: string): number {
    let mult: number;
    let degMinSec = 0;
    if (text.match(/^[W|E]\d{3,7}$/g)) {
        degMinSec = parseDegMinSec(text.substring(1), 3);
        mult = text[0] === 'W' ? -1 : 1;
    } else if (text.match(/^\d{3,7}[W|E]$/g)) {
        degMinSec = parseDegMinSec(text.substring(0, text.length - 1), 3);
        mult = text[text.length - 1] === 'W' ? -1 : 1;
    } else {
        throw new Error('Unrecognized notam format');
    }
    return +(mult * degMinSec).toFixed(6);
}

function parseDegMinSec(text: string, firstWidth: number) {
    let deg = 0;
    let min = 0;
    let sec = 0;
    if (text.length >= firstWidth) {
        deg = +text.substring(0, firstWidth);
    }

    if (text.length >= firstWidth + 2) {
        min = +text.substring(firstWidth, firstWidth + 2);
    }

    if (text.length >= firstWidth + 4) {
        sec = +text.substring(firstWidth + 2, firstWidth + 4);
    }

    return deg + min / 60 + sec / 3600;
}
